import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";
import parse from 'html-react-parser';

import { requestDesign_Aggregated } from "../../store/requests/thunks/requestDesign_Aggregated";
import { requestDesignUpdate } from "../../store/requests/thunks/requestDesignUpdate";
import { setRequestsAsIdle } from "../../store/requests/requestsSlice";

import { selectFrameAreasToRender } from "../../store/Uterum/selectFrameAreasToRender";
import { selectPartierToRender } from "../../store/Options/selectPartierToRender";

import { setSeason, setFrameType } from "../../store/Uterum/UterumSlice";

import { getColorAlias } from "../../helpers/colors";
import { getPartierAlias } from "../../helpers/partierAlias";
import { formatPrice } from "../../helpers/formatPrice";
import { alert } from "../../helpers/alert";
import { scrapePropertiesFromUrlParams } from "../../helpers/scrapePropertiesFromUrlParams";
import { getDefaultRoofForSeason } from "../../helpers/getRoofForSeason";
import { findAlias } from "../../helpers/findAlias";

import areaLabelMap from "../../json/areaLabelMap.json";

import SkeletonView from "../Skeleton/SkeletonView";

import Preview from "../../components/Preview/Preview";
import OptionList from "../../components/OptionList/OptionList";
import FrameOptions from "../../components/FrameOptions/FrameOptions";
import RoofOptions from "../../components/RoofOptions/RoofOptions";
import SeasonCompare from "../../components/SeasonCompare/SeasonCompare";

import NavigateButton from "../../designComponents/NavigateButton/NavigateButton";

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {
  _DesignViewContainer as DesignViewContainer,
  _LayoutSectionDesignView as LayoutSection,
} from "./DesignViewStyle";

const DesignView = () => {
  const dispatch = useDispatch();

  const init = useSelector((state) => state.init);
  const Uterum = useSelector((state) => state.Uterum);
  const Options = useSelector((state) => state.Options);
  const Images = useSelector((state) => state.Images);
  const Result = useSelector((state) => state.Result);
  const requests = useSelector((state) => state.requests);

  let navigate = useNavigate();

  const { assetBaseUrl, articleImageBaseUrl, baseRoute, alias } = init;

  /*
  FrameAreas (options) are returned within in the ** Uterum ** object from CGE
  (Note: For some configs, some of them should not render)
  */
  const frameAreasToRender = useSelector(selectFrameAreasToRender);

  const partierToRender = useSelector(selectPartierToRender);

  const seasonValues = [
    {
      value: "OPTIMAL",
      label: alias.bfcSummer,
      description: alias.bfcSummerShortDescription,
      iconName: "summer",
      imgAlt: alias.bfcSummer,
      disabled: false
    },
    {
      value: "ISOLENT",
      label: alias.bfcSpringFall,
      description: alias.bfcSpringFallShortDescription,
      iconName: "fall",
      imgAlt: alias.bfcSpringFall,
      disabled: false
    },
    {
      value: "ISOMAX",
      label: alias.bfcWinter,
      description: alias.bfcWinterShortDescription,
      iconName: "winter",
      imgAlt: alias.bfcWinter,
      disabled: false
    }
  ];

  const seasonOptions = () => {
    if (Options.Seasons.length === 3 && Uterum.FrameType === "LYCKA") {
      return seasonValues.map(
        season =>
          season.value === "ISOMAX" ?
            {
              ...season,
              label: alias.bfcNotAvailableWithConservatoryChoice,
              disabled: true
            } :
            season
      );
    }

    return seasonValues;
  };

  const [_1000msSinceUpdateReqInit, set1000msSinceUpdateReqInit] =
    useState(false);

  const theme = useTheme();

  // Init when view did mount --- Cleanup when view unmounts
  useEffect(() => {
    window.scrollTo(0, 0);

    const propertiesFromUrlParams = scrapePropertiesFromUrlParams();

    const hasBasicConfigFromURL =
      propertiesFromUrlParams.Season && propertiesFromUrlParams.FrameType;

    if (hasBasicConfigFromURL) {
      const {
        Season: SeasonFromUrl,
        FrameType: FrameTypeFromUrl,
        FrameWidth: FrameWidthFromUrl,
        FrameDepth: FrameDepthFromUrl,
        FrameAddonWidth: FrameAddonWidthFromUrl,
        Corners: CornersFromUrl,
        Color: ColorFromUrl,
      } = propertiesFromUrlParams;

      // Setup misc Uterum properties based on Season and FrameType
      dispatch(setSeason(SeasonFromUrl));
      dispatch(setFrameType(FrameTypeFromUrl));

      dispatch(
        requestDesign_Aggregated({
          uterumProperties: {
            ...(FrameWidthFromUrl && { FrameWidth: FrameWidthFromUrl }),
            ...(FrameDepthFromUrl && { FrameDepth: FrameDepthFromUrl }),
            ...(FrameAddonWidthFromUrl && {
              FrameAddonWidth: FrameAddonWidthFromUrl,
            }),
            ...(CornersFromUrl && { Corners: CornersFromUrl }),
            ...(ColorFromUrl && { Color: ColorFromUrl }),
          },
        })
      );
    } else {
      // Default scenario
      dispatch(requestDesign_Aggregated());
    }

    return () => {
      dispatch(
        setRequestsAsIdle(["requestDesign_Aggregated", "requestDesignUpdate"])
      );
    };
  }, [dispatch]);

  // Handle delay on pending update
  useEffect(() => {
    let timeout = null;

    if (
      requests.status.requestDesignUpdate === "pending" &&
      _1000msSinceUpdateReqInit === false
    ) {
      timeout = setTimeout(() => {
        set1000msSinceUpdateReqInit(true);
      }, 1000);
    } else if (
      requests.status.requestDesignUpdate !== "pending" &&
      _1000msSinceUpdateReqInit
    ) {
      set1000msSinceUpdateReqInit(false);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [requests.status.requestDesignUpdate]);

  // Handle rejected
  // (TODO 3062: Improve UX / error handling when requestDesignUpdate is rejected?)
  useEffect(() => {
    if (
      requests.status.requestDesign_Aggregated === "rejected" ||
      requests.status.requestDesignUpdate === "rejected"
    ) {
      alert({
        title: alias.bfcGenericErrorMessage,
        text: alias.bfcContactUs,
        icon: "error",
        confirmButtonColor: theme.primary,
        onConfirm: () => {
          navigate(`../${baseRoute}/step3`, { replace: true });
        },
      });
    }
  }, [
    requests.status.requestDesign_Aggregated,
    requests.status.requestDesignUpdate,
  ]);

  const renderMode = (() => {
    if (
      requests.status.requestDesign_Aggregated === "rejected" ||
      requests.status.requestDesignUpdate === "rejected"
    ) {
      return "none";
    }

    if (
      requests.status.requestDesign_Aggregated === "idle" ||
      requests.status.requestDesign_Aggregated === "pending" ||
      (requests.status.requestDesignUpdate === "pending" &&
        _1000msSinceUpdateReqInit)
    ) {
      return "loader";
    }

    return "default";
  })();

  if (renderMode === "loader") {
    return <SkeletonView forView="DesignView" />;
  }

  if (renderMode === "default") {
    return (
      <DesignViewContainer>
        {/* TODO använda samma SC som i SizeView? */}
        <LayoutSection>
          <Preview renderDimensions={true} dataId="Preview" />
        </LayoutSection>

        {/* TODO använda samma SC som i SizeView? */}
        <LayoutSection>
          {/* -- FrameAreas -- */}
          {frameAreasToRender && (
            <OptionList
              renderAs="thumbnails"
              desktopRenderWidth="75%"
              name="FrameAreas"
              background={theme.light}
              hideBorder={true}
              label={alias.bfcChooseSurface || ""}
              description={alias.bfcChooseSurfaceDescription}
              options={frameAreasToRender.map((frameArea) => {
                return {
                  value: frameArea.AreaCn,
                  imgSrc: `${assetBaseUrl}/store/uterumsguide2016/${Images.Images[0].ImageFolder}/area/${frameArea.AreaCn}.png`,
                  imgAlt: findAlias(
                    areaLabelMap,
                    frameArea.AreaCn,
                    alias,
                    Uterum.FrameType
                  ),
                  label: findAlias(
                    areaLabelMap,
                    frameArea.AreaCn,
                    alias,
                    Uterum.FrameType
                  ),
                };
              })}
              activeOptionValueFromParent={Uterum.SelectedFrameArea}
              onChange={(value) => {
                dispatch(
                  requestDesignUpdate({
                    uterumProperties: { SelectedFrameArea: value },
                    uterumParams: {
                      areaClicked: true,
                      area: value,
                    },
                  })
                );
              }}
            />
          )}

          {/* -- Partier -- */}
          {partierToRender?.partierArray && (
            <OptionList
              renderAs="verticalList"
              background={theme.light}
              hideBorder={
                Uterum.FrameTopWindows && Uterum.FrameTopWindows.length > 0
              }
              name="Partier"
              options={
                partierToRender.partierArray &&
                partierToRender.partierArray.map((parti) => ({
                  value: `${parti.model}|${parti.numberOfDoors}|${parti.erpItemNumber}`,
                  imgSrc: parti.imgSrc,
                  imgAlt: getPartierAlias(parti.model, alias),
                  label: getPartierAlias(parti.model, alias),
                }))
              }
              activeOptionValueFromParent={(() => {
                const activeParti = partierToRender.partierArray.find(
                  (parti) => parti.checked
                );

                if (!activeParti) {
                  console.error(
                    "activeParti not found in DesignView - partier",
                    Uterum,
                    Options,
                    Result
                  );
                  return null;
                }

                return `${activeParti.model}|${activeParti.numberOfDoors}|${activeParti.erpItemNumber}`;
              })()}
              onChange={(value) => {
                /*
                (Note: There is no property to update in Uterum)
                */
                dispatch(
                  requestDesignUpdate({
                    uterumParams: { partiArea: value },
                    meta: { dispatchedBy: "Parti" },
                  })
                );
              }}
            />
          )}

          {/* -- Windows -- */}
          {Uterum.FrameTopWindows && Uterum.FrameTopWindows.length > 0 && (
            <>
              <OptionList
                renderAs="thumbnails"
                desktopRenderWidth="75%"
                name="FrameTopAreas"
                hideBorder={true}
                background={theme.light}
                label={alias.bfcChooseTopFrame}
                description={alias.bfcChooseTopFrameDescription}
                options={Uterum.FrameTopWindows.map((window) => ({
                  value: window.AreaCn,
                  imgSrc: `${assetBaseUrl}/store/uterumsguide2016/${Images.Images[0].ImageFolder}/area/${window.AreaCn}.png`,
                  imgAlt: findAlias(areaLabelMap, window.AreaCn, alias),
                  label: findAlias(areaLabelMap, window.AreaCn, alias),
                }))}
                activeOptionValueFromParent={Uterum.SelectedTopWindow}
                onChange={(value) => {
                  dispatch(
                    requestDesignUpdate({
                      uterumProperties: { SelectedTopWindow: value },
                      uterumParams: {
                        areaClicked: true,
                        area: value,
                      },
                    })
                  );
                }}
              />
              <OptionList
                renderAs="verticalList"
                name="windowPartier"
                background={theme.light}
                options={
                  partierToRender.topPartierArray &&
                  partierToRender.topPartierArray.map((parti) => ({
                    value: `${parti.model}|${parti.numberOfDoors}|${parti.erpItemNumber}`,
                    imgSrc: parti.imgSrc,
                    imgAlt: getPartierAlias(parti.model, alias),
                    label: getPartierAlias(parti.model, alias),
                  }))
                }
                activeOptionValueFromParent={(() => {
                  const activeParti = partierToRender.topPartierArray.find(
                    (parti) => parti.checked
                  );

                  if (!activeParti) {
                    console.error(
                      "activeParti not found in DesignView - Window-partier",
                      Uterum,
                      Options,
                      Result
                    );
                    return null;
                  }

                  return `${activeParti.model}|${activeParti.numberOfDoors}|${activeParti.erpItemNumber}`;
                })()}
                onChange={(value) => {
                  /*
                (Note: There is no property to update in Uterum)
                */
                  dispatch(
                    requestDesignUpdate({
                      uterumParams: { partiArea: value },
                      meta: { dispatchedBy: "topParti" },
                    })
                  );
                }}
              />
            </>
          )}

          {/* -- Colors -- */}
          {Options.Colors && (
            <OptionList
              renderAs="thumbnails"
              desktopRenderWidth="75%"
              name="Colors"
              largePadding={true}
              label={alias.bfcChooseColor}
              description={
                Uterum.Season === "ISOMAX"
                  ? alias.bfcStep4ColorOptionDescription
                  : ""
              }
              tooltipContent={alias.tooltip_color && parse(alias.tooltip_color)}
              options={Options.Colors.map((color) => {
                const label = getColorAlias(color.Title, alias);

                return {
                  value: color.Value,
                  label,
                  imgSrc: `${assetBaseUrl}/store/uterumsguide2016/colors/${color.Value}.png`,
                  imgAlt: label,
                };
              })}
              activeOptionValueFromParent={Uterum.Color}
              onChange={(value) => {
                dispatch(
                  requestDesignUpdate({
                    uterumProperties: { Color: value },
                    uterumParams: { toggleParti: true },
                  })
                );
              }}
            />
          )}

          {/* Roof (upgrade) */}
          <RoofOptions />
          
          <Box mt={4}>
            <Typography variant="h3">{alias.bfcRecommendedAccessoires}</Typography>
          </Box>

          {/* -- WindowPanel -- */}
          {Uterum.WindowSelected && (
            <OptionList
              renderAs="select"
              name="WindowPanel"
              label={alias.bfcPanelWindow}
              // No tooltip on skbv.se
              tooltipContent=""
              options={[
                {
                  value: "false",
                  label: alias.bfcNo,
                },
                {
                  value: "true",
                  label: alias.bfcYes,
                },
              ]}
              activeOptionValueFromParent={Uterum.WindowPanel.toString()}
              onChange={(value) => {
                const bool = value === "true";
                dispatch(
                  requestDesignUpdate({
                    uterumProperties: { WindowPanel: bool },
                  })
                );
              }}
            />
          )}
          {/* -- Drains -- */}
          {Options.Accessories.Drains && Options.Accessories.Drains.length > 0 && (
            <OptionList
              renderAs="select"
              name="Drains"
              label={alias.bfcDrain}
              tooltipContent={alias.tooltip_drain && parse(alias.tooltip_drain)}
              options={(() => {
                const arr = [
                  {
                    value: "",
                    label: alias.bfcNo,
                  },
                ];

                Options.Accessories.Drains.forEach((drain) => {
                  arr.push({
                    value: JSON.stringify(drain.ErpItemNumber),
                    label: `${alias.bfcYes} ${drain.Description}`,
                    cost: formatPrice(
                      drain.Price.reduce((a, b) => a + b, 0),
                      init.currency
                    ),
                  });
                });

                return arr;
              })()}
              activeOptionValueFromParent={JSON.stringify(
                Uterum.DrainArticleErpIds
              )}
              activeImage={
                Uterum.DrainArticleErpIds &&
                  Uterum.DrainArticleErpIds.length > 0
                  ? {
                    imgSrc: `${articleImageBaseUrl}/cguide/${Uterum.DrainArticleErpIds[0]}/2?d=144x144`,
                    imgAlt: alias.bfcDrain,
                  }
                  : null
              }
              onChange={(value) => {
                const arr = JSON.parse(value);
                dispatch(
                  requestDesignUpdate({
                    uterumProperties: { DrainArticleErpIds: arr },
                  })
                );
              }}
            />
            )}
            
            {/* -- Vindskiva (Vindskena) -- */}
          {Options.Accessories.Vindskivor && (
            <OptionList
              renderAs="select"
              name="Vindskiva-Vindskena"
              label={alias.bfcVindskivaHeading}
              tooltipContent={alias.tooltip_vindskena && parse(alias.tooltip_vindskena)}
              options={(() => {
                const arr = [
                  {
                    value: "",
                    label: alias.bfcNo,
                  },
                ];

                Options.Accessories.Vindskivor.forEach((option) => {
                  arr.push({
                    value: option.ErpItemNumber,
                    label: `${alias.bfcYes} ${option.Description}`,
                    cost: formatPrice(option.Price, init.currency),
                  });
                });

                return arr;
              })()
              }
              activeOptionValueFromParent={Uterum.VindskivaErpId}
              activeImage={
                Uterum.Vindskiva
                  ? {
                    imgSrc: `${articleImageBaseUrl}/cguide/${Options.Accessories.Vindskena.ErpItemNumber}?d=144x144`,
                    imgAlt: alias.bfcVindskivaHeading,
                  }
                  : null
              }
              onChange={(value) => {
                dispatch(
                  requestDesignUpdate({ uterumProperties: { VindskivaErpId: value } })
                );
              }}
            />
          )}


          {/* -- PanelIsolation -- */}
          {/*
            If WindowSelected and WindowPanel
            or
            if any parti is "PANEL"
            --> render this option
          */}
          {((Uterum.WindowSelected && Uterum.WindowPanel) ||
            Uterum.Partier.find((parti) =>
              parti.PartierModel.startsWith("PANEL")
            )) && (
              <OptionList
                renderAs="select"
                name="PanelIsolation"
                label={alias.bfcIsolatedPanel}
                tooltipContent={alias.tooltip_isolated_panel && parse(alias.tooltip_isolated_panel)}
                options={[
                  {
                    value: "false",
                    label: alias.bfcNo,
                  },
                  {
                    value: "true",
                    label: alias.bfcYes,
                    cost: formatPrice(Result.IsolationCost, init.currency),
                  },
                ]}
                activeOptionValueFromParent={Uterum.PanelIsolation.toString()}
                onChange={(value) => {
                  const bool = value === "true";
                  dispatch(
                    requestDesignUpdate({
                      uterumProperties: { PanelIsolation: bool },
                    })
                  );
                }}
              />
              )}
            
          {/* -- Frame (primed/painted) -- */}
          {<FrameOptions background={theme.light} />}

          {/* -- Ground -- */}
          {Options.Accessories.Grounds && (Options.Accessories.Grounds.length > 0) && (
            <OptionList
              renderAs="select"
              name="Ground"
              label={alias.bfcGround}
              tooltipContent={alias.tooltip_ground && parse(alias.tooltip_ground)}
              options={(() => {
                const arr = [
                  {
                    value: "",
                    label: alias.bfcNo,
                  },
                ];

                Options.Accessories.Grounds.forEach((option) => {
                  arr.push({
                    value: option.ErpItemNumber,
                    label: `${alias.bfcYes} ${option.Description}`,
                    cost: formatPrice(option.Price, init.currency),
                  });
                });

                return arr;
              })()
              }
              activeOptionValueFromParent={Uterum.GroundErpId}
              onChange={(value) => {
                dispatch(
                  requestDesignUpdate({ uterumProperties: { GroundErpId: value } })
                );
              }}
            />
          )}

          {/* Paint (Färgpaket till stommen) */}
          {Options.Accessories &&
            Options.Accessories.Paint &&
            Options.Accessories.Paint.length > 0 && (
              <OptionList
                renderAs="select"
                name="Paint"
                label={alias.bfcPaint}
                tooltipContent={alias.tooltip_stomme_color && parse(alias.tooltip_stomme_color)}
                options={(() => {
                  const arr = [
                    {
                      value: "",
                      label: alias.bfcNo,
                    },
                  ];

                  Options.Accessories.Paint.forEach((option) => {
                    arr.push({
                      value: option.ErpItemNumber,
                      label: `${alias.bfcYes} ${option.Description}`,
                      cost: formatPrice(option.Price, init.currency),
                    });
                  });

                  return arr;
                })()}
                activeOptionValueFromParent={Uterum.PaintArticleErpId}
                activeImage={
                  Uterum.PaintArticleErpId
                    ? {
                      imgSrc: `${articleImageBaseUrl}/cguide/${Uterum.PaintArticleErpId}/1?d=144x144`,
                      imgAlt: alias.bfcPaint,
                    }
                    : null
                }
                onChange={(value) => {
                  dispatch(
                    requestDesignUpdate({
                      uterumProperties: { PaintArticleErpId: value },
                    })
                  );
                }}
              />
            )}

          {/* PaintPanel (Färgpaket till paneler) */}
          {Options.Accessories &&
            Options.Accessories.PaintPanel &&
            Options.Accessories.PaintPanel.length > 0 && (
              <OptionList
                renderAs="select"
                name="PaintPanel"
                label={alias.bfcPaintPanel}
                tooltipContent={alias.tooltip_panel_color && parse(alias.tooltip_panel_color)}
                options={(() => {
                  const arr = [
                    {
                      value: "",
                      label: alias.bfcNo,
                    },
                  ];

                  Options.Accessories.PaintPanel.forEach((option) => {
                    arr.push({
                      value: option.ErpItemNumber,
                      label: `${alias.bfcYes} ${option.Description}`,
                      cost: formatPrice(option.Price, init.currency),
                    });
                  });

                  return arr;
                })()}
                activeOptionValueFromParent={Uterum.PaintPanelArticleErpId}
                activeImage={
                  Uterum.PaintPanelArticleErpId
                    ? {
                      imgSrc: `${articleImageBaseUrl}/cguide/${Uterum.PaintPanelArticleErpId}/1?d=144x144`,
                      imgAlt: alias.bfcPaintPanel,
                    }
                    : null
                }
                onChange={(value) => {
                  dispatch(
                    requestDesignUpdate({
                      uterumProperties: { PaintPanelArticleErpId: value },
                    })
                  );
                }}
              />
            )}

          {/* WallProfiles accessory */}
          {
            Options.Accessories &&
            Options.Accessories.WallProfiles &&
            Options.Accessories.WallProfiles.length > 0 &&
            (
              <OptionList
                renderAs="select"
                name="WallProfilePanel"
                label={alias.bfcWallProfilePanel || "Väggprofiler (BYT MOT ALIAS)"}
                tooltipContent={alias.tooltip_wallprofile && parse(alias.tooltip_wallprofile)}
                options={(() => {
                  const arr = [
                    {
                      value: "",
                      label: alias.bfcNo
                    },
                  ];

                  Options.Accessories.WallProfiles.forEach(option => {
                    arr.push({
                      value: option.ErpItemNumber,
                      label: `${alias.bfcYes} ${option.Description}`,
                      cost: formatPrice(option.Price, init.currency),
                    });
                  });

                  return arr;
                })()}

                activeOptionValueFromParent={Uterum.WallProfileErpId}
                activeImage={null}
                onChange={value => {
                  dispatch(
                    requestDesignUpdate({
                      uterumProperties: { WallProfileErpId: value }
                    })
                  );
                }}
              />
            )
          }

          {/* Lamellgardiner accessory */}
          {Options.Accessories.LamellColors && (
            <Box mt={6} mb={4}>
              <Typography variant="h3">{alias.bfcPopularAccessories}</Typography>

              <OptionList
                renderAs="select"
                name="Lameller"
                label={alias.bfcLamellerHeading}
                tooltipContent={alias.tooltip_lameller && parse(alias.tooltip_lameller)}
                options={(() => {
                  const arr = [
                    {
                      value: "",
                      label: alias.bfcNo,
                    },
                  ];

                  Options.Accessories.LamellColors.forEach((option) => {
                    arr.push({
                      value: option.Key,
                      label: getColorAlias(option.Key, alias),
                      cost: formatPrice(option.Price, init.currency),
                    });
                  });

                  return arr;
                })()}
                activeOptionValueFromParent={Uterum.Lameller}
                onChange={(value) => {
                  dispatch(
                    requestDesignUpdate({
                      uterumProperties: { Lameller: value },
                    })
                  );
                }}
              />
            </Box>
            )}
          {/* Season (change) */}
          {
            <OptionList
              background={theme.light}
              renderAs="cards"
              name="Season"
              label={alias.bfcChangeSeason}
              tooltipContent={<SeasonCompare />}
              description={alias.bfcStep4SeasonOptionDescription}
              options={seasonOptions()}
              activeOptionValueFromParent={Uterum.Season}
              onChange={(value) => {
                const Season = value;
                const Roof = getDefaultRoofForSeason(Season);

                !Roof &&
                  console.error(
                    `DesignView: Season onChange; no Roof found for selected Season: ${Season}, Uterum: ${JSON.stringify(
                      Uterum
                    )}`
                  );

                dispatch(
                  requestDesignUpdate({
                    uterumProperties: {
                      Season,
                      Roof,
                      PanelIsolation: Season === "ISOMAX",
                      ...((
                        // ISOMAX = "Året runt"/vinter 
                        Season === "ISOMAX" && Uterum.Color === "GREY"
                        ) && {
                        Color: "WHITE",
                      }),
                      ...((
                        Season === "ISOMAX" && Uterum.Color === "MATT_BLACK" && (Uterum.FrameType === "SADEL" || Uterum.FrameType === "SADEL_BROKEN" ) 
                        ) && {
                        Color: "WHITE",
                      }),
                      ...((
                        Season === "ISOMAX" && Uterum.Color === "MATT_BLACK" && Uterum.FrameType !== "SADEL" && Uterum.FrameType !== "SADEL_BROKEN"
                        ) && {
                        Color: "TEXTURED_BLACK",
                      }),
                      // OPTIMAL = "Sommar", ISOLENT = "Vår till höst"
                      ...((
                        (Season === "OPTIMAL" || Season === "ISOLENT") && Uterum.Color === "TEXTURED_BLACK"
                        ) && {
                        Color: "MATT_BLACK",
                      }),
                    },
                    uterumParams: {
                      toggleParti: true,
                    },
                  })
                );
              }}
            />
          }

          <Box mt={2}>
            <NavigateButton
              handleClick={() => navigate(`../${baseRoute}/step5`)}
              dataId={"NextStepButton"}
              text={alias.bfcNextStep}
            />
          </Box>
          
          
        </LayoutSection>
      </DesignViewContainer>
    );
  }

  /*
  If status for request(s) is 'rejected' --> renderMode is "none"
  --> error handling for UI is done in useEffect
  */
  return null;
};

export default DesignView;
